import CustomScrollbar from 'components/Common/CustomScrollbar';
import React, { useEffect, useState, useMemo, useLayoutEffect, useRef } from 'react';
import { Portal } from 'react-portal';

// Constants
const NAVIGATION_TOP_OFFSET = 67;
const STICKY_TITLE_OFFSET = 20;
const FADE_OUT_DELAY = 1000;
const FADE_OUT_DURATION = 500;
const MIN_WIDTH = 20;
const MAX_WIDTH = 45;
const WIDTH_RANGE = MAX_WIDTH - MIN_WIDTH;

// Helper functions

const getContentContainer = (container) => {
  // Prefer the specific context container if present
  return (
    container?.querySelector('.py-3.news-content-in.parliamentary-content-in.context') ||
    container?.querySelector('.main-update-content-container')
  );
};

const getStickyTitleHeight = (container, contentContainer) => {
  const stickyTitle =
    container?.querySelector('.content-item-scrolled-title.item-scrolled-title-visible') ||
    contentContainer?.querySelector('.content-item-scrolled-title.item-scrolled-title-visible') ||
    container?.querySelector('.content-item-scrolled-title') ||
    contentContainer?.querySelector('.content-item-scrolled-title');

  if (!stickyTitle) return 0;

  // If the sticky title is visible, use its current height
  if (stickyTitle.offsetHeight > 0) {
    return stickyTitle.offsetHeight;
  }

  // If the sticky title is hidden (display: none), temporarily make it visible to measure
  // This is important for the first click when the sticky title hasn't appeared yet
  const originalDisplay = stickyTitle.style.display;
  const originalVisibility = stickyTitle.style.visibility;
  const originalPosition = stickyTitle.style.position;

  stickyTitle.style.display = 'block';
  stickyTitle.style.visibility = 'hidden';
  stickyTitle.style.position = 'absolute';

  const height = stickyTitle.offsetHeight || stickyTitle.getBoundingClientRect().height;

  // Restore original styles
  stickyTitle.style.display = originalDisplay;
  stickyTitle.style.visibility = originalVisibility;
  stickyTitle.style.position = originalPosition;

  return height;
};

const getPositionRelativeToContainer = (element, container) => {
  if (!element || !container) return 0;

  const elementRect = element.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();

  // Position of element's top relative to the container's content area,
  // accounting for the container's current scroll position.
  return elementRect.top - containerRect.top + container.scrollTop;
};

const calculateSectionHeight = (targetElement, sectionIndex, sortedSections, contentContainer) => {
  const getPosition = (element) => getPositionRelativeToContainer(element, contentContainer);
  const currentSectionTop = getPosition(targetElement);

  if (sectionIndex < sortedSections.length - 1) {
    const nextSection = sortedSections[sectionIndex + 1];
    const nextSectionElement = document.getElementById(nextSection.sectionId);

    if (nextSectionElement && contentContainer.contains(nextSectionElement)) {
      return getPosition(nextSectionElement) - currentSectionTop;
    }
  }

  return contentContainer.scrollHeight - currentSectionTop;
};

const removeActiveSectionClass = (contentContainer) => {
  if (!contentContainer) return;
  const activeElements = contentContainer.querySelectorAll('.content-active-section');
  activeElements.forEach((element) => {
    element.classList.remove('content-active-section');
    element.style.removeProperty('--section-indicator-height');
  });
};

const ContentNavigation = (props) => {
  const { navigationSections, scrollbarRef } = props;
  const [summaryContentTop, setSummaryContentTop] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const [activeSectionIndex, setActiveSectionIndex] = useState(0);

  const contentNavigationRef = useRef(null);
  const navigationScrollbarRef = useRef(null);
  const popupScrollbarRef = useRef(null);
  const popupContainerRef = useRef(null);
  const timeoutRef = useRef(null);
  const activeIndexRef = useRef(0);

  useEffect(() => {
    let animationFrameId;

    const updateSummaryContentTop = () => {
      const container = scrollbarRef.current?.container;
      if (!container) {
        animationFrameId = window.requestAnimationFrame(updateSummaryContentTop);
        return;
      }
      const summaryContent = container.querySelector('.summary-content');
      setSummaryContentTop(summaryContent?.offsetTop ?? 0);
    };

    updateSummaryContentTop();

    return () => {
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [scrollbarRef, navigationSections]);

  const sortedSections = useMemo(() => {
    if (!navigationSections) return [];
    return [...navigationSections].sort((a, b) => a.displayOrder - b.displayOrder);
  }, [navigationSections]);

  const calculateWidth = useMemo(() => {
    if (!sortedSections?.length) return () => MIN_WIDTH;

    const characterLengths = sortedSections.map((section) => section.sectionLengthInCharacters || 0);
    const minLength = Math.min(...characterLengths);
    const maxLength = Math.max(...characterLengths);
    const range = maxLength - minLength;

    return (sectionLengthInCharacters) => {
      if (range === 0) return (MIN_WIDTH + MAX_WIDTH) / 2;
      const normalized = (sectionLengthInCharacters - minLength) / range;
      const width = MIN_WIDTH + normalized * WIDTH_RANGE;
      return Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, width));
    };
  }, [sortedSections]);

  const calculateScrollPosition = (sectionIndex) => {
    if (!scrollbarRef.current?.view || !sortedSections.length) return 0;
    if (sectionIndex < 0 || sectionIndex >= sortedSections.length) return 0;

    const section = sortedSections[sectionIndex];
    if (!section?.sectionId) return 0;

    const targetElement = document.getElementById(section.sectionId);
    if (!targetElement) return 0;

    const { view, container } = scrollbarRef.current;
    const contentContainer = getContentContainer(container);
    if (!contentContainer?.contains(targetElement)) return 0;

    // Use position relative to the scrollable view element instead of viewport-relative getBoundingClientRect
    // This is more reliable, especially on the first click when element might not be in viewport
    const elementPosition = getPositionRelativeToContainer(targetElement, view);

    // Get sticky title height - it should be calculated based on whether it will appear
    // We need to account for the sticky title that will appear when scrolling to this position
    const stickyTitleHeight = getStickyTitleHeight(container, contentContainer);
    const offset = stickyTitleHeight + STICKY_TITLE_OFFSET;

    // Calculate scroll position: element position minus the offset
    const scrollPosition = elementPosition - offset;

    return Math.max(0, scrollPosition);
  };

  const applyActiveSectionClass = (sectionIndex) => {
    if (!scrollbarRef.current?.container || !sortedSections.length) return;
    if (sectionIndex < 0 || sectionIndex >= sortedSections.length) return;

    const section = sortedSections[sectionIndex];
    if (!section?.sectionId) return;

    const container = scrollbarRef.current.container;
    const contentContainer = getContentContainer(container);
    if (!contentContainer) return;

    removeActiveSectionClass(contentContainer);

    const targetElement = document.getElementById(section.sectionId);
    if (!targetElement || !contentContainer.contains(targetElement)) return;

    targetElement.classList.add('content-active-section');
    const sectionHeight = calculateSectionHeight(targetElement, sectionIndex, sortedSections, contentContainer);

    if (sectionHeight > 0) {
      targetElement.style.setProperty('--section-indicator-height', `${sectionHeight}px`);
    }
  };

  useEffect(() => {
    const scrollElement = scrollbarRef?.current?.view;
    if (!scrollElement || sortedSections.length === 0) return;

    const calculateActiveSection = (scrollTop) => {
      const container = scrollbarRef.current.container;
      // Use the specific context container for sectionTops
      const contextContainer = container?.querySelector('.specific-content-container');
      const contentContainer = getContentContainer(container);
      if (!contextContainer || !contentContainer || sortedSections.length === 0) return 0;

      // Get top positions of each section relative to the context container
      const sectionTops = sortedSections.map((section) => {
        const el = document.getElementById(section.sectionId);
        if (el && contextContainer.contains(el)) {
          return getPositionRelativeToContainer(el, contextContainer);
        }
        return null;
      });
      // Find the section whose top is closest but not greater than scrollTop
      let activeIndex = 0;
      for (let i = 0; i < sectionTops.length; i++) {
        if (sectionTops[i] !== null && scrollTop >= sectionTops[i]) {
          activeIndex = i;
        } else {
          break;
        }
      }
      return activeIndex;
    };

    const handleScroll = () => {
      const scrollTop = scrollElement.scrollTop;
      const activeIndex = calculateActiveSection(scrollTop);
      const prevIndex = activeIndexRef.current;
      activeIndexRef.current = activeIndex;
      setActiveSectionIndex(activeIndex);
      if (prevIndex !== activeIndex) {
        scrollToActiveInNavigation(activeIndex);
      }
    };

    // Set initial active section
    handleScroll();

    scrollElement.addEventListener('scroll', handleScroll);
    return () => {
      scrollElement.removeEventListener('scroll', handleScroll);
    };
  }, [scrollbarRef, sortedSections]);

  // Cleanup timeout on unmount or when navigationSections change to prevent cross-item side effects
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [navigationSections]);

  const cleanupActiveSection = (sectionId) => {
    const targetElement = document.getElementById(sectionId);
    if (targetElement) {
      targetElement.classList.remove('content-active-section');
      targetElement.style.removeProperty('--section-indicator-height');
    }
  };

  const handleSectionClick = (sectionIndex) => {
    // Clear any existing timeout to prevent cross-item side effects
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    applyActiveSectionClass(sectionIndex);

    const scrollPosition = calculateScrollPosition(sectionIndex);
    if (scrollbarRef.current?.view) {
      scrollbarRef.current.view.scrollTo({
        top: scrollPosition,
        behavior: 'smooth',
      });
    }

    const section = sortedSections[sectionIndex];
    if (section?.sectionId) {
      timeoutRef.current = setTimeout(() => {
        cleanupActiveSection(section.sectionId);
        timeoutRef.current = null;
      }, FADE_OUT_DELAY + FADE_OUT_DURATION);
    }

    setIsHovered(false);
  };

  const scrollToActiveInNavigation = (activeIndex) => {
    const activeItem = contentNavigationRef.current?.querySelectorAll('.content-navigation-item')[activeIndex];
    if (activeItem && navigationScrollbarRef.current?.view) {
      activeItem.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'auto' });
    }
    const activePopupButton = popupContainerRef.current?.querySelectorAll('button')[activeIndex];
    if (activePopupButton && popupScrollbarRef.current?.view) {
      activePopupButton.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'auto' });
    }
  };

  const updatePopupPosition = () => {
    if (contentNavigationRef.current && isHovered) {
      const rect = contentNavigationRef.current.getBoundingClientRect();
      setPopupPosition({
        top: rect.top,
        left: rect.left,
      });
    }
  };

  useLayoutEffect(() => {
    if (isHovered) {
      updatePopupPosition();
      window.addEventListener('resize', updatePopupPosition);
      window.addEventListener('scroll', updatePopupPosition, true);

      const scrollElement = scrollbarRef?.current?.view;
      if (scrollElement) {
        scrollElement.addEventListener('scroll', updatePopupPosition);
      }

      return () => {
        window.removeEventListener('resize', updatePopupPosition);
        window.removeEventListener('scroll', updatePopupPosition, true);
        if (scrollElement) {
          scrollElement.removeEventListener('scroll', updatePopupPosition);
        }
      };
    }
  }, [isHovered, scrollbarRef, contentNavigationRef]);

  useEffect(() => {
    if (!isHovered) return;

    const animationFrameId = window.requestAnimationFrame(() => {
      scrollToActiveInNavigation(activeSectionIndex);
    });

    return () => {
      window.cancelAnimationFrame(animationFrameId);
    };
  }, [isHovered, activeSectionIndex]);

  return (
    <div
      className='content-navigation-container'
      style={{ top: `${summaryContentTop + NAVIGATION_TOP_OFFSET}px` }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      ref={contentNavigationRef}
    >
      <CustomScrollbar
        ref={navigationScrollbarRef}
        className={'simple-scrollbar'}
        style={{ height: `${window.innerHeight - 370}px`, maxHeight: '300px' }}
        minimalThumbYSize={55}
        maximalThumbYSize={100}
      >
        <div className='content-navigation'>
          {sortedSections.map((section, index) => {
            const width = calculateWidth(section.sectionLengthInCharacters || 0);
            const isActive = index === activeSectionIndex;
            return (
              <div
                key={section.sectionId}
                className={`content-navigation-item ${isActive ? 'active' : ''}`}
                style={{ width: `${width}px` }}
              ></div>
            );
          })}
        </div>
      </CustomScrollbar>
      {isHovered && (
        <Portal>
          <div
            ref={popupContainerRef}
            className='position-fixed z-extra-max bg-white rounded py-2 border overflow-auto'
            style={{
              minWidth: '250px',
              maxHeight: `${window.innerHeight - popupPosition.top - 30}px`,
              top: `${popupPosition.top}px`,
              left: `${popupPosition.left}px`,
            }}
          >
            <CustomScrollbar
              ref={popupScrollbarRef}
              className={'simple-scrollbar'}
              autoHeight
              autoHeightMin='100%'
              autoHeightMax={`${window.innerHeight - popupPosition.top - 60}px`}
              minimalThumbYSize={55}
              maximalThumbYSize={100}
              maxWidth={250}
            >
              {sortedSections.map((section, index) => {
                const isActive = index === activeSectionIndex;
                return (
                  <button
                    key={section.sectionId}
                    className={`w-100 d-block text-left general-button list-action-button px-3 py-2 reset-link reset-link-color  ${isActive ? 'text-main-blue font-weight-semibold' : 'text-main-blue-65'}`}
                    onClick={() => handleSectionClick(index)}
                  >
                    {section.sectionTitle || `Section ${section.displayOrder}`}
                  </button>
                );
              })}
            </CustomScrollbar>
          </div>
        </Portal>
      )}
    </div>
  );
};

export default ContentNavigation;
